import {
  ResponseTimeOperator,
  ResponseType,
  TimeUnit,
  type ResponseTimeCondition as ResponseTimeConditionType,
} from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

const responseTypeOptions = [
  {
    id: ResponseType.MERCHANT,
    type: "text" as const,
    text: "Merchant response",
    value: ResponseType.MERCHANT,
  },
  {
    id: ResponseType.CUSTOMER,
    type: "text" as const,
    text: "Customer response",
    value: ResponseType.CUSTOMER,
  },
  {
    id: ResponseType.EITHER,
    type: "text" as const,
    text: "Any response",
    value: ResponseType.EITHER,
  },
];

const operatorOptions = [
  {
    id: ResponseTimeOperator.MORE_THAN,
    type: "text" as const,
    text: "More than",
    value: ResponseTimeOperator.MORE_THAN,
  },
  {
    id: ResponseTimeOperator.LESS_THAN,
    type: "text" as const,
    text: "Less than",
    value: ResponseTimeOperator.LESS_THAN,
  },
  {
    id: ResponseTimeOperator.BETWEEN,
    type: "text" as const,
    text: "Between",
    value: ResponseTimeOperator.BETWEEN,
  },
];

const unitOptions = [
  {
    id: TimeUnit.MINUTES,
    type: "text" as const,
    text: "minutes",
    value: TimeUnit.MINUTES,
  },
  {
    id: TimeUnit.HOURS,
    type: "text" as const,
    text: "hours",
    value: TimeUnit.HOURS,
  },
  {
    id: TimeUnit.DAYS,
    type: "text" as const,
    text: "days",
    value: TimeUnit.DAYS,
  },
];

export const ResponseTimeCondition = memo(function ResponseTimeCondition({
  condition,
  onChange,
  editableFields,
}: {
  condition: ResponseTimeConditionType;
  onChange: (updated: ResponseTimeConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const isEditable = (f: string) =>
    editableFields === undefined || editableFields.has(f);
  const responseTypeEditable = isEditable("responseType");
  const operatorEditable = isEditable("responseTimeOperator");
  const valueEditable = isEditable("value");
  const minMaxEditable = isEditable("minValue") || isEditable("maxValue");
  const unitEditable = isEditable("unit");

  const selectedResponseType = useMemo(
    () =>
      responseTypeOptions.find((opt) => opt.value === condition.responseType),
    [condition.responseType],
  );

  const selectedOperator = useMemo(
    () =>
      operatorOptions.find(
        (opt) => opt.value === condition.responseTimeOperator,
      ),
    [condition.responseTimeOperator],
  );

  const selectedUnit = useMemo(
    () => unitOptions.find((opt) => opt.value === condition.unit),
    [condition.unit],
  );

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput
        disabled={!responseTypeEditable}
        options={responseTypeOptions}
        optionSelected={({ item }) => {
          if (!responseTypeEditable) return;
          onChange({ ...condition, responseType: item.value });
        }}
        placeholder="Select response type"
        selectedItem={selectedResponseType}
      />
      <RedoSingleSelectDropdownInput
        disabled={!operatorEditable}
        options={operatorOptions}
        optionSelected={({ item }) => {
          if (!operatorEditable) return;
          const base = {
            conditionType: condition.conditionType,
            responseType: condition.responseType,
            unit: condition.unit,
          } as const;
          if (item.value === ResponseTimeOperator.BETWEEN) {
            onChange({
              ...base,
              responseTimeOperator: item.value,
              minValue: 0,
              maxValue: 0,
            });
          } else {
            onChange({ ...base, responseTimeOperator: item.value, value: 0 });
          }
        }}
        placeholder="Select operator"
        selectedItem={selectedOperator}
      />
      {(condition.responseTimeOperator === ResponseTimeOperator.MORE_THAN ||
        condition.responseTimeOperator === ResponseTimeOperator.LESS_THAN) && (
        <Flex align="center" gap="sm">
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(val: string) => {
              if (!valueEditable) return;
              const num = parseFloat(val);
              onChange({ ...condition, value: isNaN(num) ? 0 : num });
            }}
            size="sm"
            state={valueEditable ? "default" : "readonly"}
            value={condition.value.toString()}
          />
          <RedoSingleSelectDropdownInput
            disabled={!unitEditable}
            options={unitOptions}
            optionSelected={({ item }) => {
              if (!unitEditable) return;
              onChange({ ...condition, unit: item.value });
            }}
            selectedItem={selectedUnit}
          />
        </Flex>
      )}
      {condition.responseTimeOperator === ResponseTimeOperator.BETWEEN && (
        <Flex align="center" gap="sm">
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(val: string) => {
              if (!minMaxEditable) return;
              const num = parseFloat(val);
              onChange({ ...condition, minValue: isNaN(num) ? 0 : num });
            }}
            size="sm"
            state={minMaxEditable ? "default" : "readonly"}
            value={condition.minValue.toString()}
          />
          <Text color="secondary" fontSize="sm">
            and
          </Text>
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(val: string) => {
              if (!minMaxEditable) return;
              const num = parseFloat(val);
              onChange({ ...condition, maxValue: isNaN(num) ? 0 : num });
            }}
            size="sm"
            state={minMaxEditable ? "default" : "readonly"}
            value={condition.maxValue.toString()}
          />
          <RedoSingleSelectDropdownInput
            disabled={!unitEditable}
            options={unitOptions}
            optionSelected={({ item }) => {
              if (!unitEditable) return;
              onChange({ ...condition, unit: item.value });
            }}
            selectedItem={selectedUnit}
          />
        </Flex>
      )}
    </Flex>
  );
});
